import {
  ChevronRight,
  FileText,
  Folder,
  Layout,
  Mail,
  Plus,
  Trash2,
} from "lucide-react";
import React, { useMemo } from "react";
import AiRephraseIcon from "./AiRephraseIcon";

const ContractBoqTable = ({
  items,
  addDefaultItems,
  handleAIRephrase,
  handleUpdateItem,
  setModalOpen,
  setModalType,
  setSelectedRowIndex,
  selectedTemplates,
  setItems,
  loading,
}) => {
  const [collapsedIds, setCollapsedIds] = React.useState(new Set());

  // --- Accordion Logic ---
  const toggleAccordion = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setCollapsedIds(new Set());

  const collapseAll = () => {
    const parentIds = items
      .filter((item) => item.level < 3)
      .map((item) => item.id);
    setCollapsedIds(new Set(parentIds));
  };

  // --- Optimized Visibility Logic ---
  // Calculates which rows are visible in a single pass
  const visibleItems = useMemo(() => {
    const visible = [];
    const hiddenParents = new Set();

    items.forEach((item) => {
      // Check if any of this item's parents are in the hiddenParents set
      let isHiddenByParent = false;
      // If a level 1 is collapsed, all its level 2s and 3s are hidden
      // We track which hierarchy paths are currently "closed"

      // Simple logic: if parent is collapsed, children are hidden
      // We look at the current state of ancestors
      const isVisible = !items.some((potentialParent, idx) => {
        const itemIdx = items.indexOf(item);
        return (
          idx < itemIdx &&
          potentialParent.level < item.level &&
          collapsedIds.has(potentialParent.id) &&
          // Ensure it's the actual parent (not a parent of a previous branch)
          !items
            .slice(idx + 1, itemIdx)
            .some((p) => p.level <= potentialParent.level)
        );
      });

      if (isVisible) visible.push(item);
    });
    return visible;
  }, [items, collapsedIds]);

  const insertItem = (index, level) => {
    const newItem = {
      id: Math.random().toString(36).substr(2, 9),
      level,
      label: "",
      qty: 0,
      rate: 0,
      costCode: "",
      margin: 0,
      tenderRate: 0,
    };
    const newItems = [...items];
    newItems.splice(index + 1, 0, newItem);
    setItems(newItems);
  };

  // --- WBS Logic ---
  const getWBSCode = (index) => {
    let l1 = 0,
      l2 = 0,
      l3 = 0;
    for (let i = 0; i <= index; i++) {
      if (items[i].level === 1) {
        l1++;
        l2 = 0;
        l3 = 0;
      } else if (items[i].level === 2) {
        l2++;
        l3 = 0;
      } else if (items[i].level === 3) {
        l3++;
      }
    }
    const item = items[index];
    if (item.level === 1) return `${l1}.0`;
    if (item.level === 2) return `${l1}.${l2}`;
    return `${l1}.${l2}.${l3}`;
  };

  const getRowStyles = (level) => {
    switch (level) {
      case 1:
        return "bg-slate-900 text-white border-l-4 border-blue-600 shadow-sm uppercase tracking-wider";
      case 2:
        return "bg-slate-100 text-slate-800 border-l-4 border-slate-400 font-semibold";
      default:
        return "bg-white text-slate-600 border-l-4 border-transparent hover:bg-slate-50";
    }
  };

  return (
    <div className="bg-white border rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-[60px_1fr_80px_100px_120px_100px_80px_100px_40px] bg-slate-800 p-4 text-[10px] uppercase tracking-wider text-white font-black items-center">
        <div className="px-2 text-slate-400">WBS</div>
        <div className="px-2">Description</div>
        <div className="text-center">Qty</div>
        <div className="text-right px-2">Actual Price</div>
        <div className="text-right px-2">Amount</div>
        <div className="text-center px-2">Cost Code</div>
        <div className="text-center px-2">Margin %</div>
        <div className="text-right px-2">Tender Price</div>
        <div></div>
      </div>

      {loading ? (
        <div className="p-12 flex flex-col items-center justify-center text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <p className="text-sm text-slate-500 font-medium">
            Generating Bill of Quantities...
          </p>
        </div>
      ) : items.length === 0 ? (
        <div className="p-12 flex flex-col items-center justify-center text-center">
          <div className="bg-slate-50 p-6 rounded-full mb-4">
            <Layout size={40} className="text-slate-300" />
          </div>
          <p className="mb-4 text-sm text-slate-500 max-w-xs">
            Your Bill of Quantities is empty. Start by adding a default template
            or a new phase.
          </p>
          <button
            onClick={addDefaultItems}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all shadow-md"
          >
            Initialize Table
          </button>
        </div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto overflow-x-hidden">
          {items.map((item, index) => {
            if (!visibleItems.includes(item)) return null;
            const isCollapsed = collapsedIds.has(item.id);

            return (
              <div key={item.id} className="group relative">
                <div
                  className={`grid grid-cols-[60px_1fr_80px_100px_120px_100px_80px_100px_40px] items-center py-3 px-4 transition-all ${getRowStyles(item.level)}`}
                >
                  {/* WBS Number */}
                  <div className="text-[10px] font-mono font-bold opacity-60">
                    {getWBSCode(index)}
                  </div>

                  {/* Description Column */}
                  <div className="flex items-center min-w-0">
                    {item.level === 3 ? (
                      <AiRephraseIcon
                        item={item}
                        handleAIRephrase={handleAIRephrase}
                        handleUpdateItem={handleUpdateItem}
                        setModalOpen={setModalOpen}
                        setModalType={setModalType}
                        index={index}
                        setSelectedRowIndex={setSelectedRowIndex}
                      />
                    ) : (
                      <div
                        className="flex items-center gap-2 w-full cursor-pointer"
                        style={{ paddingLeft: `${(item.level - 1) * 16}px` }}
                        onClick={() => toggleAccordion(item.id)}
                      >
                        <ChevronRight
                          size={16}
                          className={`transition-transform duration-200 ${!isCollapsed ? "rotate-90" : ""} ${item.level === 1 ? "text-blue-400" : "text-slate-400"}`}
                        />
                        {item.level === 1 ? (
                          <Folder
                            size={16}
                            className="text-yellow-500 shrink-0"
                          />
                        ) : (
                          <FileText
                            size={14}
                            className="text-slate-400 shrink-0"
                          />
                        )}
                        <input
                          className="w-full bg-transparent border-none focus:ring-0 text-sm font-bold truncate placeholder:text-slate-400/50"
                          value={item.label}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            handleUpdateItem(index, { label: e.target.value })
                          }
                          placeholder={
                            item.level === 1 ? "PHASE NAME..." : "GROUP NAME..."
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Level 3 Specific Inputs */}
                  <div className="px-1 text-center">
                    {item.level === 3 && (
                      <input
                        type="number"
                        className="w-full bg-slate-100/50 rounded text-xs p-1 text-center font-mono focus:bg-white outline-none"
                        value={item.qty}
                        onChange={(e) =>
                          handleUpdateItem(index, {
                            qty: parseFloat(e.target.value) || 0,
                          })
                        }
                      />
                    )}
                  </div>

                  <div className="px-2 text-right">
                    {item.level === 3 && (
                      <input
                        type="number"
                        className="w-full text-right bg-slate-100/50 rounded text-xs p-1 font-mono focus:bg-white outline-none"
                        value={item.rate}
                        onChange={(e) =>
                          handleUpdateItem(index, {
                            rate: parseFloat(e.target.value) || 0,
                          })
                        }
                      />
                    )}
                  </div>

                  <div className="text-right px-2 text-xs font-mono font-bold text-slate-500">
                    {item.level === 3
                      ? (item.qty * item.rate).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })
                      : ""}
                  </div>

                  <div className="px-2 text-center">
                    {item.level === 3 && (
                      <input
                        className="w-full text-center bg-slate-50 border rounded text-[10px] p-1 uppercase focus:bg-white outline-none"
                        value={item.costCode}
                        onChange={(e) =>
                          handleUpdateItem(index, { costCode: e.target.value })
                        }
                        placeholder="CODE"
                      />
                    )}
                  </div>

                  <div className="px-2 text-center">
                    {item.level === 3 && (
                      <input
                        type="number"
                        className="w-full text-center bg-blue-50 border border-blue-100 rounded text-xs p-1 font-bold text-blue-600 focus:bg-white outline-none"
                        value={item.margin}
                        onChange={(e) =>
                          handleUpdateItem(index, {
                            margin: parseFloat(e.target.value) || 0,
                          })
                        }
                      />
                    )}
                  </div>

                  <div className="px-2 text-right">
                    {item.level === 3 && (
                      <input
                        type="number"
                        className="w-full text-right bg-emerald-50 border border-emerald-100 rounded text-xs p-1 font-bold text-emerald-700 focus:bg-white outline-none"
                        value={item.tenderRate}
                        onChange={(e) =>
                          handleUpdateItem(index, {
                            tenderRate: parseFloat(e.target.value) || 0,
                          })
                        }
                      />
                    )}
                  </div>

                  {/* Delete Row */}
                  <div className="flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() =>
                        setItems(items.filter((_, i) => i !== index))
                      }
                      className="text-red-400 hover:text-red-600 transition-colors p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Floating Insert Buttons */}
                <div className="absolute -bottom-[14px] left-0 right-0 z-30 flex justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none translate-y-1 group-hover:translate-y-0">
                  <div className="bg-white border-2 border-blue-500 shadow-xl rounded-full px-1 py-1 flex gap-1 scale-90 pointer-events-auto">
                    <button
                      onClick={() => insertItem(index, 1)}
                      className="flex items-center gap-1 text-[9px] font-black px-3 py-1 hover:bg-slate-100 border-r text-slate-700 uppercase"
                    >
                      <Folder size={10} className="text-yellow-500" /> Phase
                    </button>
                    <button
                      onClick={() => insertItem(index, 2)}
                      className="flex items-center gap-1 text-[9px] font-black px-3 py-1 hover:bg-slate-100 border-r text-slate-700 uppercase"
                    >
                      <FileText size={10} className="text-blue-500" /> Group
                    </button>
                    <button
                      onClick={() => insertItem(index, 3)}
                      className="flex items-center gap-1 text-[9px] font-black px-3 py-1 hover:bg-blue-50 text-blue-600 rounded-r-full uppercase"
                    >
                      <Plus size={10} /> Item
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer Controls */}
      <div className="bg-slate-50 p-4 border-t flex justify-between items-center">
        <div className="flex items-center gap-6">
          <button
            onClick={() => setItems([])}
            className="flex items-center gap-2 text-[10px] font-bold text-red-500 hover:text-red-700 transition-colors uppercase tracking-tight"
          >
            <Trash2 size={14} /> Clear Grid
          </button>

          <div className="h-4 w-[1px] bg-slate-300"></div>

          <div className="flex items-center gap-3">
            <button
              onClick={expandAll}
              className="text-[10px] font-bold text-blue-600 hover:underline uppercase"
            >
              Expand All
            </button>
            <span className="text-slate-300 text-xs">/</span>
            <button
              onClick={collapseAll}
              className="text-[10px] font-bold text-slate-500 hover:underline uppercase"
            >
              Collapse All
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => {
              setModalType("emailPreview");
              setModalOpen(true);
            }}
            className={`
    px-4 py-2 rounded-md text-xs font-semibold transition-all border flex items-center gap-2
    shadow-sm hover:shadow-md transform active:scale-95 bg-white text-slate-500 hover:bg-slate-50 border-slate-200
    
  `}
          >
            <div className="bg-blue-100 p-1 rounded text-blue-600">
              <Mail size={12} fill="currentColor" fillOpacity={0.2} />
            </div>
            <div className="flex flex-col items-start">
              <span className="leading-none">Email Preview</span>
            </div>
          </button>

          <button
            onClick={() => {
              setModalType("header");
              setModalOpen(true);
            }}
            className={`px-4 py-2 rounded-md text-xs font-bold transition-all border flex items-center gap-2 ${selectedTemplates.header ? "bg-blue-600 border-blue-700 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            <Layout size={14} />{" "}
            {selectedTemplates.header ? "Header Selected" : "Header Details"}
          </button>

          <button
            onClick={() => {
              setModalType("footer");
              setModalOpen(true);
            }}
            className={`px-4 py-2 rounded-md text-xs font-bold transition-all border flex items-center gap-2 ${selectedTemplates.footer ? "bg-blue-600 border-blue-700 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            <FileText size={14} />{" "}
            {selectedTemplates.footer ? "Footer Selected" : "Footer Details"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractBoqTable;
